import { useEffect, useState } from "react";
import clsx from "clsx";
import { X } from "lucide-react";

const ACCENT_BORDER = {
    info: "border-blue-500",
    success: "border-green-500",
    warning: "border-amber-500",
    error: "border-red-500",
};

const PROGRESS_BG = {
    info: "bg-blue-500",
    success: "bg-green-500",
    warning: "bg-amber-500",
    error: "bg-red-500",
};

/**
 * One notice bubble. Owns its own timer; calls onClose when the duration
 * elapses or the user clicks the close button. Hover pauses, mouseleave
 * fully resets the timer (and the progress bar animation).
 */
export const NoticeItem = ({ id, type, message, duration, onClose }) => {
    const [hovered, setHovered] = useState(false);
    const [restartKey, setRestartKey] = useState(0);

    useEffect(() => {
        if (hovered) return undefined;
        const t = setTimeout(() => onClose(id), duration);
        return () => clearTimeout(t);
    }, [hovered, restartKey, duration, onClose, id]);

    const handleEnter = () => setHovered(true);
    const handleLeave = () => {
        setHovered(false);
        setRestartKey((k) => k + 1);
    };

    return (
        <div
            className={clsx(
                "pointer-events-auto relative min-w-[280px] max-w-[420px] overflow-hidden rounded-none border-l-4 bg-slate-800 px-4 py-3 pb-3.5 text-sm text-slate-50 shadow-lg",
                ACCENT_BORDER[type] || ACCENT_BORDER.info,
            )}
            data-type={type}
            role={type === "error" ? "alert" : "status"}
            aria-live={type === "error" ? "assertive" : "polite"}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 whitespace-pre-line break-words leading-snug">{message}</div>
                <button
                    type="button"
                    onClick={() => onClose(id)}
                    aria-label="Fermer la notification"
                    className="shrink-0 text-slate-300 transition hover:text-white"
                >
                    <X size={16} />
                </button>
            </div>
            <div
                key={restartKey}
                data-paused={hovered ? "true" : "false"}
                className={clsx(
                    "rj-notice-progress absolute bottom-0 left-0 h-0.5 w-full",
                    PROGRESS_BG[type] || PROGRESS_BG.info,
                )}
                style={{ animationDuration: `${duration}ms` }}
            />
        </div>
    );
};
